import { useEffect, useId, useRef, useState } from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { Popover } from './Popover'
import { cx } from '@/lib/cx'

interface Props {
  label: string
  value: string
  onChange: (value: string) => void
  /** Suggestions, not a closed list: a caller may name a place the clinic has
   *  not sat at yet. */
  options: string[]
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
  placeholder?: string
  disabled?: boolean
}

/**
 * A text field with suggestions. Replaces `<datalist>`, whose popup is drawn by
 * the browser: it cannot be themed, it is invisible in the dark theme on some
 * platforms, and it silently does nothing at all on several mobile browsers.
 */
export function Combobox({
  label,
  value,
  onChange,
  options,
  required = false,
  error,
  hint,
  placeholder,
  disabled = false,
}: Props) {
  const listId = `${useId()}-listbox`
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const wrap = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  const matches = options.filter(
    (option) => !value.trim() || option.toLowerCase().includes(value.trim().toLowerCase()),
  )

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!matches.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => Math.min(matches.length - 1, i + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (event.key === 'Enter' && open && active >= 0) {
      event.preventDefault()
      onChange(matches[active]!)
      setOpen(false)
    }
  }

  return (
    <Field label={label} required={required} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <div className="relative" ref={wrap}>
          <input
            ref={input}
            id={id}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value)
              setActive(-1)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className={cx(FIELD_CONTROL, invalid && FIELD_INVALID)}
          />

          <Popover
            anchor={input}
            open={open && matches.length > 0}
            className="max-h-56 overflow-y-auto rounded-md border border-line bg-raised p-1 shadow-lg"
          >
            <ul id={listId} role="listbox">
              {matches.map((option, index) => (
                <li
                  key={option}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={option === value}
                  onPointerDown={(event) => {
                    event.preventDefault()
                    onChange(option)
                    setOpen(false)
                  }}
                  onPointerMove={() => setActive(index)}
                  className={cx(
                    'flex min-h-touch cursor-pointer items-center rounded-sm px-3 text-sm',
                    index === active ? 'bg-hover text-fg' : 'text-fg-muted',
                  )}
                >
                  {option}
                </li>
              ))}
            </ul>
          </Popover>
        </div>
      )}
    </Field>
  )
}
